// Every call to the billing API carries the session of whoever is signed in.
//
// The API used to act for the account named in the request body; most pages
// therefore called it with a plain fetch() and no session. It now takes the
// account from the login (2026-09-29), so the session has to travel with every
// request. Rather than touching fifty call sites — and breaking the one that
// gets forgotten — fetch itself adds the header when:
//   - the request goes to the billing API (API_BASE, or the OCR endpoint), and
//   - the caller did not set an Authorization header of its own, and
//   - somebody is signed in (public pages — pay links, tracking, catalogues —
//     have no session and are sent exactly as before).
// Requests to anything else (Supabase, storage, maps…) are never touched.
import { supabase } from './supabase';
import { getFreshToken } from './session';
import { API_BASE } from './api';

const originOf = (url) => {
    try { return new URL(url, typeof window !== 'undefined' ? window.location.href : undefined).origin; } catch (e) { return null; }
};

/** Origins that are the billing API. */
export function apiOrigins(env = process.env) {
    return [API_BASE, env.REACT_APP_OCR_URL].map(u => (u ? originOf(u) : null)).filter(Boolean);
}

const urlOf = (input) => (typeof input === 'string' ? input : (input && (input.url || input.href)) || String(input || ''));

const headersOf = (input, init) => {
    const own = init && init.headers;
    const inherited = !own && input && typeof input === 'object' && input.headers;
    return new Headers(own || inherited || undefined);
};

/** Whether this request should be given the session. */
export function wantsSession(input, init, origins) {
    const origin = originOf(urlOf(input));
    if (!origin || !origins.includes(origin)) return false;
    return !headersOf(input, init).has('Authorization');
}

/** The same request with the session added. */
export function withSession(input, init, token) {
    const headers = headersOf(input, init);
    headers.set('Authorization', `Bearer ${token}`);
    return { ...(init || {}), headers };
}

// Only a signed-in visitor has a token; a public page must not trigger a
// session refresh (there is nothing to refresh).
async function currentToken() {
    const { data } = await supabase.auth.getSession();
    if (!data || !data.session) return null;
    return getFreshToken();
}

export function installApiAuth(target = window, { token = currentToken, origins = apiOrigins() } = {}) {
    if (!target || typeof target.fetch !== 'function' || target.__zbApiAuth) return;
    const plain = target.fetch.bind(target);
    target.fetch = async (input, init) => {
        let options = init;
        try {
            if (wantsSession(input, init, origins)) {
                const t = await token();
                if (t) options = withSession(input, init, t);
            }
        } catch (e) { /* the request goes out as it was; the API answers what to do */ }
        return plain(input, options);
    };
    target.__zbApiAuth = true;
}
